import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Subtree } from "subtree.js/react";
import { pick } from "trunk.js";
import type { AppDeps } from "../../app-deps";
import { detectBarcode } from "../../core/primitives/barcode";
import { CardCamera } from "../../core/services/card-camera";
import { pictureFromFile } from "../../core/services/scan-image";
import { ScanCardController } from "./scan-card-controller";
import { ScanCardPage } from "./scan-card-page";
import { scannerExit } from "./scanner";

/**
 * `?customer=<id>` scans for a counter customer, from their page. A page that opens the scanner passes its own path
 * as `returnTo` in the router state, so Done goes back to it when that is where the scanner lands.
 */
export function ScanCardRoute({ deps }: { deps: AppDeps }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const customerId = params.get("customer");
  const openedFrom = (location.state as { returnTo?: unknown } | null)?.returnTo;
  return (
    <Subtree
      key="scan-card"
      deps={[customerId]}
      controller={() =>
        new ScanCardController(
          {
            ...pick(deps, "scanSessions", "storeRepository", "broadcastStudio"),
            camera: new CardCamera({
              devices: () => navigator.mediaDevices ?? null,
              secure: () => window.isSecureContext,
            }),
            readPicture: pictureFromFile,
            detectBarcode,
            vibrate: () => {
              if ("vibrate" in navigator) navigator.vibrate(10);
            },
          },
          {
            done: () => {
              const exit = scannerExit(customerId, openedFrom);
              if ("back" in exit) navigate(-1);
              else navigate(exit.to, { replace: true });
            },
          },
          customerId,
        )
      }
    >
      <ScanCardPage />
    </Subtree>
  );
}
